import { useCallback } from 'react'

import { reportErrorMessageWithData } from 'src/lib/js/clientError'
import useTracker from 'src/lib/js/hooks/useTracker'

import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { useCart } from 'public/components/online_ordering/CartContext'

import config from 'config'

import {
  RECAPTCHA_ACTION_CREATE_PAYMENT_INTENT,
  RECAPTCHA_TOKEN_MAX_ATTEMPTS,
  ReCaptchaSource
} from './constants'
import {
  ReCaptchaUnavailableError,
  generateReCaptchaTokenWithRetries
} from './generateReCaptchaToken'

/**
 * Why token generation gave up. The two cases call for different guest-facing
 * messaging, so they stay distinct all the way out to the UI.
 */
export enum ReCaptchaFailure {
  /**
   * `grecaptcha` never appeared on the page — an extension or network filter is
   * blocking the script, or it simply never loaded. Retrying won't help; the
   * guest has to unblock it.
   */
  Blocked = 'BLOCKED',
  /**
   * The library was there but never produced a usable token: timeouts, an
   * `execute` rejection, or an empty token. Often transient, so worth retrying.
   */
  Unverified = 'UNVERIFIED'
}

/**
 * Neither field set means no token was asked for (flag off) — that is a success,
 * not a failure.
 */
export type ReCaptchaTokenResult = {
  token?: string
  failure?: ReCaptchaFailure
}

/**
 * Returns a callback that generates a reCAPTCHA Enterprise token for online
 * ordering payment intent creation, gated on a single flag,
 * `ooGenerateRecaptchaTokenForIntent`.
 *
 * Flag off: no reCAPTCHA work at all — the callback resolves an empty result and
 * the mutation goes out without a token.
 *
 * Flag on: callers await the token and forward it in the mutation input. The wait
 * is bounded — up to RECAPTCHA_TOKEN_MAX_ATTEMPTS attempts on the stepped budgets
 * in RECAPTCHA_TOKEN_ATTEMPT_TIMEOUTS_MS, with a backoff only when the script
 * isn't on the page yet.
 *
 * Fail-open: once every attempt has failed the callback resolves with a
 * `ReCaptchaFailure` and no token, so the mutation still proceeds — and the caller
 * can explain the failure to the guest if the mutation is rejected for it.
 * Failures are reported to Sentry and Amplitude.
 *
 * IMPORTANT: Server side defaulted feature flags can have a variable context key
 * based on the request context. That means we cannot currently trust one feature
 * flag across backend and frontend to enforce token generation. As a result
 * `oo-generate-recaptcha-token-for-intent` is expected to either be set to 100%
 * on or 0% off at any given point in time. If server validation or enforcement
 * of recaptcha token is enabled, then this additional flag must be universally
 * enabled as well.
 */
export const useGenerateReCaptchaTokenForPayment = () => {
  const tracker = useTracker()
  const { cartGuid } = useCart()
  const { ooGenerateRecaptchaTokenForIntent } = useFlags()

  return useCallback(async (): Promise<ReCaptchaTokenResult> => {
    if (!ooGenerateRecaptchaTokenForIntent) {
      return {}
    }

    let classifiedFailure: ReCaptchaFailure | undefined

    const token = await generateReCaptchaTokenWithRetries(
      config.reCaptchaEnterprise.siteKey,
      RECAPTCHA_ACTION_CREATE_PAYMENT_INTENT,
      (lastError, durationMs) => {
        const failure =
          lastError instanceof ReCaptchaUnavailableError
            ? ReCaptchaFailure.Blocked
            : ReCaptchaFailure.Unverified
        classifiedFailure = failure
        const failureData = {
          source: ReCaptchaSource.PAYMENT_INTENT,
          action: RECAPTCHA_ACTION_CREATE_PAYMENT_INTENT,
          durationMs,
          attempts: RECAPTCHA_TOKEN_MAX_ATTEMPTS,
          failure,
          message: lastError?.message,
          cartGuid
        }
        reportErrorMessageWithData(
          'Error generating reCAPTCHA token for payment intent',
          failureData
        )
        tracker.track(
          TrackingEvents.RECAPTCHA_TOKEN_GENERATION_FAILED,
          failureData
        )
      }
    )

    if (token === undefined) {
      return { failure: classifiedFailure ?? ReCaptchaFailure.Unverified }
    }

    return { token }
  }, [ooGenerateRecaptchaTokenForIntent, tracker, cartGuid])
}
